type Align = 'left' | 'right' | 'center' | 'default';

function isSeparatorLine(line: string): boolean {
  let s = line.trim();
  if (!s.includes('|') || !s.includes('-')) return false;
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  const cells = s.split('|').map((c) => c.trim());
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{2,}:?$/.test(c) || /^:?-+:?$/.test(c));
}

function splitRow(line: string): string[] {
  let s = line.trim();
  const lead = s.startsWith('|');
  const trail = s.endsWith('|');
  if (lead) s = s.slice(1);
  if (trail) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function parseAlign(cell: string): Align {
  const c = cell.trim();
  const l = c.startsWith(':');
  const r = c.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  if (l) return 'left';
  return 'default';
}

function pad(s: string, w: number, align: Align): string {
  const diff = w - s.length;
  if (diff <= 0) return s;
  if (align === 'right') return ' '.repeat(diff) + s;
  if (align === 'center') {
    const left = Math.floor(diff / 2);
    return ' '.repeat(left) + s + ' '.repeat(diff - left);
  }
  return s + ' '.repeat(diff);
}

function sepCell(w: number, align: Align): string {
  const width = Math.max(3, w);
  if (align === 'center') return ':' + '-'.repeat(width - 2) + ':';
  if (align === 'right') return '-'.repeat(width - 1) + ':';
  if (align === 'left') return ':' + '-'.repeat(width - 1);
  return '-'.repeat(width);
}

export function formatTables(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const header = lines[i];
    const next = lines[i + 1];
    if (
      header &&
      header.includes('|') &&
      next !== undefined &&
      isSeparatorLine(next)
    ) {
      const hasOuter = header.trim().startsWith('|');
      let j = i + 2;
      while (
        j < lines.length &&
        lines[j].includes('|') &&
        lines[j].trim() !== '' &&
        !isSeparatorLine(lines[j])
      ) {
        j++;
      }

      const headerCells = splitRow(header);
      const sepCells = splitRow(next);
      const aligns = sepCells.map(parseAlign);
      const rows: string[][] = [];
      for (let k = i + 2; k < j; k++) rows.push(splitRow(lines[k]));

      const cols = Math.max(
        headerCells.length,
        sepCells.length,
        ...rows.map((r) => r.length),
      );
      const ensure = (arr: string[]) => {
        while (arr.length < cols) arr.push('');
      };
      ensure(headerCells);
      ensure(sepCells);
      rows.forEach(ensure);
      while (aligns.length < cols) aligns.push('default');

      const widths = new Array(cols).fill(3);
      for (let c = 0; c < cols; c++) {
        widths[c] = Math.max(widths[c], headerCells[c].length);
        for (const r of rows) widths[c] = Math.max(widths[c], r[c].length);
      }

      const renderRow = (cells: string[]) => {
        const inner = cells
          .map((c, k) => pad(c, widths[k], aligns[k]))
          .join(' | ');
        return hasOuter ? `| ${inner} |` : inner;
      };
      const renderSep = () => {
        const inner = widths.map((w, k) => sepCell(w, aligns[k])).join(' | ');
        return hasOuter ? `| ${inner} |` : inner;
      };

      out.push(renderRow(headerCells));
      out.push(renderSep());
      for (const r of rows) out.push(renderRow(r));
      i = j;
      continue;
    }
    out.push(lines[i]);
    i++;
  }
  return out.join('\n');
}
